/**
 * Phase 21 — single source of truth for "where should this user land?".
 *
 * Used by every flow that needs to bounce an already-authenticated, already-
 * verified user to their preferred surface: post-login navigation, the
 * VerifyEmail "you're already verified" branches, the RootLanding component,
 * and the post-reset-password flow.
 *
 * Centralising this guarantees that:
 *   1. Verified users NEVER land on /verify-email or /auth as a side-effect.
 *   2. Each user gets their `default_tab` preference (Capture / Feed / Creator
 *      / Investor / etc.) honoured every time they re-enter the app.
 *   3. Future tab additions only need to be wired here, not in five places.
 */

const FEED_TABS = new Set([
  'main', 'creator', 'investor', 'crowdfunder', 'dating', 'marketplace',
]);

export function getHomePath(user) {
  // Unauthenticated → sign-in.
  if (!user) return '/auth';
  // Phase 23 — email verification REMOVED. The previous email_verified gate
  // here used to forcibly bounce unverified sessions back to /auth; that
  // path is gone now. Any authenticated user goes straight to their
  // preferred tab.
  const tab = (user.default_tab || 'capture').toString();
  if (tab === 'capture') return '/capture';
  if (tab.startsWith('feed/') || tab.startsWith('/feed')) return `/${tab.replace(/^\//, '')}`;
  if (FEED_TABS.has(tab)) return `/feed/${tab}`;
  // Anything we don't recognise — Capture is the safest default since it's
  // the universal "post something" entrypoint.
  return '/capture';
}

/**
 * Resolve the navigation target after a successful auth event (login,
 * register, password-reset → re-login). Honors a `dest` from
 * `location.state.from` when it's a real interior page; otherwise falls
 * back to the user's preferred home.
 *
 * The `dest === '/verify-email'` exclusion is critical: the previous bug
 * was that ProtectedRoute saved `loc.pathname` ("/verify-email") when an
 * unauthenticated user clicked the email link, then AuthPage would route
 * them BACK to that page after login — producing the verification-loop UX
 * the user reported.
 */
export function postAuthDestination(user, requestedDest) {
  const home = getHomePath(user);
  if (!requestedDest) return home;
  // Strip query/hash — fall back to the user's home for any soft-blocked
  // destination (root, /auth — the previous /verify-email special-case has
  // been removed in Phase 23 along with the rest of the verification flow).
  const path = String(requestedDest).split('?')[0].split('#')[0];
  if (!path || path === '/' || path === '/auth' || path === '/verify-email') {
    return home;
  }
  return requestedDest;
}
